import { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { RefreshCw, ArrowLeftRight, WifiOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../api';
import { reportSyncState } from '../helper/syncElectron';

const baseUrlRemote = process.env.REACT_APP_BASE_URL_REMOTE?.startsWith("http")
    ? process.env.REACT_APP_BASE_URL_REMOTE
    : `https://${process.env.REACT_APP_BASE_URL_REMOTE}`;

// Cap that must stay in sync with backend SYNC_BATCH_SIZE. Anything above
// this would be rejected with HTTP 413 by both /sync/push-and-pull and
// /sync/upload.
const BATCH_SIZE = 200;
const DONE_ID_CHUNK = 400; // /sync/done caps at SYNC_BATCH_SIZE * 2 = 400 on the default cap.

/**
 * Split an array into fixed-size chunks. Pure utility, kept here so the
 * sync flow is self-contained.
 */
const chunk = (arr, size) => {
    if (!Array.isArray(arr) || arr.length === 0) return [];
    const out = [];
    for (let i = 0; i < arr.length; i += size) {
        out.push(arr.slice(i, i + size));
    }
    return out;
};

export const SyncComponent = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    // Progress tracking for paginated /sync/changes
    const [syncProgress, setSyncProgress] = useState({ fetched: 0, total: 0 });

    // Track network connectivity
    useEffect(() => {
        const goOnline = () => setIsOnline(true);
        const goOffline = () => setIsOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    // Mirror syncLoading into the Electron main process so it can
    // prompt the user before letting them close the window mid-sync.
    useEffect(() => {
        reportSyncState(syncLoading);
    }, [syncLoading]);

    const showModal = () => setIsModalVisible(true);
    const handleClose = () => {
        if (syncLoading) return; // Prevent closing during active transmission
        setIsModalVisible(false);
    };

    /**
     * Paginated bidirectional sync. All five hops (local drain, push,
     * pull, local-apply, cleanup) are chunked so that no single HTTP
     * body can exceed body-parser's limit.
     */
    const handleBidirectionalSync = async () => {
        if (!isOnline) {
            toast.error("Synchronization unavailable. Please check your internet connection.");
            return;
        }

        setSyncLoading(true);
        setSyncProgress({ fetched: 0, total: 0 });

        // Aggregated state across all sync stages
        let totalChangesProcessed = 0;
        let totalConflicts = 0;
        let totalErrors = 0;
        let allRemoteCleanupIds = [];
        let lastRemoteCheckpoint = null;

        // Logger so we can trace what each stage actually did when the toast
        // says "0 synced" or numbers don't add up.
        const log = (...args) => console.log("[Sync]", ...args);

        try {
            // -------- 1. Drain the local change log (paginated GET) --------
            log("STAGE 1 — draining local change log");
            const localChangesQueue = [];
            const localChangeIds = [];
            let skip = 0;
            while (true) {
                const pageRes = await axios.get(`${API_BASE_URL}/sync/changes`, {
                    params: { limit: BATCH_SIZE, skip }
                });
                const { changes = [], total = 0, hasMore = false, nextSkip = null } = pageRes.data || {};
                localChangesQueue.push(...changes);
                setSyncProgress({ fetched: localChangesQueue.length, total });

                log(`  page skip=${skip} returned=${changes.length} total=${total} hasMore=${hasMore}`);

                if (!hasMore || changes.length === 0) break;
                // Use nextSkip if server provided it; else fall back to count
                skip = typeof nextSkip === "number" ? nextSkip : (skip + changes.length);
            }
            log(`STAGE 1 OK — drained ${localChangesQueue.length} local changes`);

            // Topological dependency order weights
            const collectionWeights = {
                counter: -1,
                admin: 0, testcategory: 0, textinfo: 0,
                patient: 1, doctor: 1, manager: 1, agent: 1, test: 1,
                testorder: 2, activity: 2,
                report: 3
            };

            localChangesQueue.sort((a, b) => {
                const wA = collectionWeights[a.collectionName?.toLowerCase()] ?? 2;
                const wB = collectionWeights[b.collectionName?.toLowerCase()] ?? 2;
                return wA - wB;
            });

            // -------- 2. Push to remote in batches --------
            const lastSyncedAt = localStorage.getItem('system_last_synced_at') || new Date(0).toISOString();
            log(`STAGE 2 — pushing to remote (inboundBatches=${Math.ceil(localChangesQueue.length / BATCH_SIZE)}, lastSyncedAt=${lastSyncedAt})`);
            const inboundBatches = chunk(localChangesQueue, BATCH_SIZE);

            // Track server-side changes needing local application
            const serverChangesToApply = [];
            // Track server change IDs we need to clean up once applied
            const remoteChangeIdsSeen = new Set();

            // Use a rolling cutoff so each batch's `since` advances to the
            // checkpoint from the previous batch's response. Otherwise we'd
            // either (a) re-pull server changes we already pulled or
            // (b) miss server changes that landed between calls.
            let rollingSince = new Date(new Date(lastSyncedAt).getTime() - 1000).toISOString();

            if (inboundBatches.length === 0) {
                const emptyBatch = [];
                log(`  no inbound changes — sending empty push-and-pull to fetch server delta`);
                try {
                    const emptyPushRes = await axios.post(`${baseUrlRemote}/sync/push-and-pull`, {
                        changes: emptyBatch,
                        lastSyncedAt: rollingSince,
                        serverChangesLimit: BATCH_SIZE
                    });
                    const epd = emptyPushRes.data || {};
                    serverChangesToApply.push(...(epd.serverChanges || []));
                    (epd.serverChangesIds || []).forEach(id => remoteChangeIdsSeen.add(id));
                    if (epd.syncedAt) {
                        lastRemoteCheckpoint = epd.syncedAt;
                        rollingSince = epd.syncedAt;
                    }
                    log(`    empty push-and-pull -> serverChanges=${epd.serverChanges?.length || 0}/${epd.serverChangesTotal || 0} hasMore=${epd.serverChangesHasMore}`);
                    if (epd.serverChangesHasMore) {
                        let remoteSkip = (epd.serverChanges || []).length;
                        let safetyHops = 0;
                        const MAX_HOPS = 200;
                        while (safetyHops < MAX_HOPS) {
                            safetyHops += 1;
                            log(`    draining remote server-changes since=${rollingSince} skip=${remoteSkip}`);
                            let more;
                            try {
                                more = await axios.get(`${baseUrlRemote}/sync/server-changes`, {
                                    params: { since: rollingSince, limit: BATCH_SIZE, skip: remoteSkip }
                                });
                            } catch (moreErr) {
                                throw moreErr;
                            }
                            const md = more.data || {};
                            if ((md.changes || []).length === 0 && !md.hasMore) break;
                            serverChangesToApply.push(...(md.changes || []));
                            (md.changes || []).forEach(c => c?._id && remoteChangeIdsSeen.add(c._id.toString()));
                            if (!md.hasMore) break;
                            remoteSkip = typeof md.nextSkip === "number" ? md.nextSkip : (remoteSkip + (md.changes || []).length);
                        }
                    }
                } catch (emptyErr) {
                    console.error("[Sync] STAGE 2 FAIL — empty push-and-pull threw:", emptyErr.response?.data || emptyErr.message);
                    throw emptyErr;
                }
            }

            for (let i = 0; i < inboundBatches.length; i++) {
                const batch = inboundBatches[i];
                log(`  inbound batch #${i + 1}/${inboundBatches.length} size=${batch.length} since=${rollingSince}`);
                batch.forEach((c, idx) => {
                    log(`    [PUSH ITEM #${idx + 1}] Collection: ${c.collectionName} | Op: ${c.operation} | DocID: ${c.documentId} | LogID: ${c._id}`);
                });

                let pushRes;
                try {
                    pushRes = await axios.post(`${baseUrlRemote}/sync/push-and-pull`, {
                        changes: batch,
                        lastSyncedAt: rollingSince,
                        serverChangesLimit: BATCH_SIZE
                    });
                } catch (pushErr) {
                    console.error("[Sync] STAGE 2 FAIL — push-and-pull threw:", pushErr.response?.data || pushErr.message);
                    // Re-throw so the outer catch surfaces a single toast.
                    throw pushErr;
                }

                const {
                    accepted = [],
                    conflicts = [],
                    errors = [],
                    serverChanges = [],
                    serverChangesIds = [],
                    serverChangesTotal = 0,
                    serverChangesHasMore = false,
                    syncedAt
                } = pushRes.data || {};

                log(`    push-and-pull -> accepted=${accepted.length} conflicts=${conflicts.length} errors=${errors.length} serverChanges=${serverChanges.length}/${serverChangesTotal} hasMore=${serverChangesHasMore} syncedAt=${syncedAt}`);

                // Only clean up change log records that Remote explicitly accepted or resolved in conflicts
                accepted.forEach(id => localChangeIds.push(id));
                conflicts.forEach(c => c?._id && localChangeIds.push(c._id));

                totalConflicts += conflicts.length;
                totalErrors += errors.length;
                totalChangesProcessed += batch.length;

                // Stage server-side changes for local apply
                serverChangesToApply.push(...serverChanges);
                serverChangesIds.forEach(id => remoteChangeIdsSeen.add(id));

                // Advance the cutoff to the server's freshly-issued timestamp
                // so the NEXT batch's pull only sees NEW server changes.
                if (syncedAt) {
                    lastRemoteCheckpoint = syncedAt;
                    rollingSince = syncedAt;
                }

                setSyncProgress({
                    fetched: totalChangesProcessed,
                    total: localChangesQueue.length + serverChangesTotal
                });

                // Drain remaining server-side pages via the dedicated endpoint
                // when ANY batch returns serverChangesHasMore=true (was previously
                // limited to i===0, which leaked un-drained server changes on
                // later batches).
                if (serverChangesHasMore) {
                    let remoteSkip = serverChanges.length;
                    let safetyHops = 0;
                    const MAX_HOPS = 200; // 200 * BATCH_SIZE = 40k entries; bail if exceeded
                    while (safetyHops < MAX_HOPS) {
                        safetyHops += 1;
                        log(`    draining remote server-changes since=${rollingSince} skip=${remoteSkip}`);

                        let more;
                        try {
                            more = await axios.get(`${baseUrlRemote}/sync/server-changes`, {
                                params: {
                                    since: rollingSince,
                                    limit: BATCH_SIZE,
                                    skip: remoteSkip
                                }
                            });
                        } catch (moreErr) {
                            console.error("[Sync]    server-changes drain FAIL:", moreErr.response?.data || moreErr.message);
                            throw moreErr;
                        }
                        const {
                            changes: moreChanges = [],
                            total: moreTotal = 0,
                            hasMore: moreHasMore = false,
                            nextSkip: moreNextSkip = null
                        } = more.data || {};
                        if (moreChanges.length === 0 && !moreHasMore) break;

                        serverChangesToApply.push(...moreChanges);
                        // NOTE: /server-changes doesn't return IDs — we collect the
                        // _id field of each change directly.
                        moreChanges.forEach(c => c?._id && remoteChangeIdsSeen.add(c._id.toString()));

                        const grandTotal = localChangesQueue.length + (moreTotal || serverChangesTotal);
                        setSyncProgress({
                            fetched: totalChangesProcessed,
                            total: grandTotal
                        });

                        if (!moreHasMore) break;
                        remoteSkip = typeof moreNextSkip === "number" ? moreNextSkip : (remoteSkip + moreChanges.length);
                    }
                    if (safetyHops >= MAX_HOPS) {
                        console.warn("[Sync]    server-changes drain hit MAX_HOPS, bailing to avoid infinite loop");
                    }
                }
            }

            allRemoteCleanupIds = Array.from(remoteChangeIdsSeen);
            const uniqueLocalCleanupIds = Array.from(new Set(localChangeIds.filter(Boolean)));
            log(`STAGE 2 OK — pushed ${totalChangesProcessed}, serverChanges collected=${serverChangesToApply.length}, localCleanupIds=${uniqueLocalCleanupIds.length}, remoteCleanupIds=${allRemoteCleanupIds.length}`);

            // -------- 3. Apply server-side changes to local DB in batches --------
            // CRITICAL: Filter out server changes for documents we just pushed.
            // Without this, stale server change-log entries for the same documents
            // would overwrite the local edits we just synced to the remote.
            const pushedDocIds = new Set(
                localChangesQueue.map(c => c.documentId).filter(Boolean)
            );
            const filteredServerChanges = serverChangesToApply.filter(
                sc => !pushedDocIds.has(sc.documentId)
            );
            const skippedCount = serverChangesToApply.length - filteredServerChanges.length;
            if (skippedCount > 0) {
                log(`STAGE 3 — filtered out ${skippedCount} server changes that overlap with just-pushed documents`);
            }
            log(`STAGE 3 — applying ${filteredServerChanges.length} server changes locally in batches (${skippedCount} skipped)`);
            const uploadBatches = chunk(filteredServerChanges, BATCH_SIZE);
            for (let u = 0; u < uploadBatches.length; u++) {
                const batch = uploadBatches[u];
                if (batch.length === 0) continue;
                try {
                    const uploadRes = await axios.post(`${API_BASE_URL}/sync/upload`, { changes: batch });
                    log(`  upload batch #${u + 1}/${uploadBatches.length} size=${batch.length} ->`, uploadRes.data);
                } catch (uploadErr) {
                    console.error(`[Sync]   upload batch #${u + 1} FAIL:`, uploadErr.response?.data || uploadErr.message);
                    throw uploadErr;
                }
            }
            log(`STAGE 3 OK`);

            // -------- 4. Cleanup the local change log (pushed work is done) --------
            log(`STAGE 4 — cleaning up local change log (${uniqueLocalCleanupIds.length} ids)`);
            let localDeleted = 0;
            const localCleanupChunks = chunk(uniqueLocalCleanupIds, DONE_ID_CHUNK);
            for (let c = 0; c < localCleanupChunks.length; c++) {
                const idChunk = localCleanupChunks[c];
                if (idChunk.length === 0) continue;
                try {
                    const r = await axios.put(`${API_BASE_URL}/sync/done`, { ids: idChunk });
                    localDeleted += r.data?.deletedCount || 0;
                    log(`  local cleanup chunk #${c + 1} size=${idChunk.length} -> deletedCount=${r.data?.deletedCount}`);
                    if ((r.data?.deletedCount || 0) < idChunk.length) {
                        console.warn(`[Sync]   local cleanup under-deleted: requested=${idChunk.length} got=${r.data?.deletedCount}`);
                    }
                } catch (cleanupErr) {
                    console.error("[Sync]  LOCAL CLEANUP FAILED:", cleanupErr.response?.data || cleanupErr.message);
                    toast.error(`Local change-log cleanup failed: ${cleanupErr.response?.status || cleanupErr.message} — these changes will re-push next sync.`);
                }
            }
            log(`STAGE 4 OK — deleted ${localDeleted} local change-log rows`);

            // -------- 5. Cleanup the remote change log (server-side) --------
            log(`STAGE 5 — cleaning up remote change log (${allRemoteCleanupIds.length} ids)`);
            let remoteDeleted = 0;
            const remoteCleanupChunks = chunk(allRemoteCleanupIds, DONE_ID_CHUNK);
            for (let c = 0; c < remoteCleanupChunks.length; c++) {
                const idChunk = remoteCleanupChunks[c];
                if (idChunk.length === 0) continue;
                try {
                    const r = await axios.put(`${baseUrlRemote}/sync/done`, { ids: idChunk });
                    remoteDeleted += r.data?.deletedCount || 0;
                    log(`  remote cleanup chunk #${c + 1} size=${idChunk.length} -> deletedCount=${r.data?.deletedCount}`);
                    if ((r.data?.deletedCount || 0) < idChunk.length) {
                        console.warn(`[Sync]   remote cleanup under-deleted: requested=${idChunk.length} got=${r.data?.deletedCount}`);
                    }
                } catch (remoteErr) {
                    console.error("[Sync]  REMOTE CLEANUP FAILED:", remoteErr.response?.data || remoteErr.message);
                    toast.error(`Remote change-log cleanup failed: ${remoteErr.response?.status || remoteErr.message} — server will re-pull these next sync.`);
                }
            }
            log(`STAGE 5 OK — deleted ${remoteDeleted} remote change-log rows`);

            // -------- 6. Surface diagnostics --------
            if (totalConflicts > 0) {
                toast.warning(`${totalConflicts} conflicts reverted to Server-Wins version.`);
            }
            if (totalErrors > 0) {
                console.error(`${totalErrors} sync adjustments failed processing`);
            }

            // -------- 7. Commit checkpoint + reload --------
            if (lastRemoteCheckpoint) {
                localStorage.setItem('system_last_synced_at', lastRemoteCheckpoint);
                log(`STAGE 7 — committed checkpoint ${lastRemoteCheckpoint}`);
            }
            toast.success(
                `Bidirectional sync complete. ${totalChangesProcessed} local change(s) processed; ${serverChangesToApply.length} server change(s) applied.`
            );
            handleClose();
            setTimeout(() => {
                window.location.reload();
            }, 3000);

        } catch (error) {
            console.error("Critical Sync Breakdown:", error);
            toast.error(`Sync Pipeline Failure: ${error.response?.data?.message || error.message}`);
        } finally {
            setSyncLoading(false);
            setSyncProgress({ fetched: 0, total: 0 });
        }
    };

    return (
        <>
            <button
                onClick={showModal}
                className="group relative px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-md hover:shadow-lg active:scale-95"
            >
                <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                <span>Data Sync Hub</span>
            </button>

            <Modal
                open={isModalVisible}
                onCancel={handleClose}
                footer={null}
                width={520}
                centered
                closable={!syncLoading}
                maskStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.3)', backdropFilter: 'blur(8px)' }}
                styles={{
                    content: {
                        borderRadius: '24px',
                        padding: 0,
                        overflow: 'hidden',
                        background: '#ffffff'
                    }
                }}
            >
                {/* Header Container */}
                <div className="bg-gradient-to-br from-slate-900 to-blue-950 p-8 text-white text-center relative">
                    <div className="mx-auto w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/15 mb-4">
                        {syncLoading ? (
                            <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
                        ) : !isOnline ? (
                            <WifiOff className="w-8 h-8 text-red-400" />
                        ) : (
                            <ArrowLeftRight className="w-8 h-8 text-emerald-400" />
                        )}
                    </div>
                    <h3 className="text-xl font-bold tracking-tight">Bidirectional Sync Hub</h3>
                    <p className="text-slate-400 text-xs mt-1">
                        Last Checkpoint: {localStorage.getItem('system_last_synced_at') ? new Date(localStorage.getItem('system_last_synced_at')).toLocaleString() : 'Never Synced'}
                    </p>
                </div>

                {/* Dashboard Interface Container */}
                <div className="p-6 space-y-6">
                    {!isOnline && (
                        <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 p-3.5 rounded-xl text-sm">
                            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                            <div>
                                <span className="font-semibold block">Offline Mode Active</span>
                                Operational networks are offline. Reconnect to standard broadband web interfaces to push updates.
                            </div>
                        </div>
                    )}

                    {/* <div className="text-slate-600 text-sm leading-relaxed text-center">
                        This workflow executes an atomic push-and-pull operation. It safely transfers local offline updates up to the remote instance while simultaneously fetching recent server adjustments.
                    </div> */}

                    <button
                        onClick={handleBidirectionalSync}
                        disabled={syncLoading || !isOnline}
                        className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-3 shadow-sm transition-all duration-200
                            ${syncLoading
                                ? 'bg-slate-100 text-slate-400 cursor-wait'
                                : !isOnline
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.99] hover:shadow-md'}`}
                    >
                        {syncLoading ? (
                            <div className="flex flex-col items-center gap-2 w-full">
                                <div className="flex items-center gap-3">
                                    <RefreshCw className="w-5 h-5 animate-spin" />
                                    <span>
                                        {syncProgress.total > 0
                                            ? `Syncing ${syncProgress.fetched} / ${syncProgress.total}...`
                                            : 'Processing Pipeline Sync...'}
                                    </span>
                                </div>
                                {syncProgress.total > 0 && (
                                    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 transition-all duration-300"
                                            style={{ width: `${Math.min(100, Math.round((syncProgress.fetched / syncProgress.total) * 100))}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <ArrowLeftRight className="w-5 h-5" />
                                <span>Sync Environments Now</span>
                            </>
                        )}
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default SyncComponent;